import { telefonoParaMostrar } from "../../../helpers/telefonoWhatsapp";
import React, { useEffect, useMemo, useState } from "react";
import { Row, Col } from "react-bootstrap";
import OrganizerPillSelect from "../../components/OrganizerPillSelect";
import TablePagination from "../../components/TablePagination";
import { WhatsappLink } from "../../components/Whatsapp";
import RecordatorioLineas from "../../recordatorios/RecordatorioLineas";
import { formatAxisMinute } from "../../reservas/components/dailyCourtGridLayout";
import {
  estadoVisible,
  minutosPreparacion,
  nombreCliente,
  plata,
  puedeCancelar,
  puedeConfirmar,
  puedeMarcarIngreso,
  puedeMover,
  puedeNoShow,
  serviciosLabel,
  statusMeta,
} from "./agendaLayout";
import "../../reservas/reservas-organizer.css";
import "../turnos-beauty.css";
import SerieChip from "./SerieChip";
import "./agenda.css";

/* ─────────────────────────────────────────
   BeautyAgendaTabla — vista "Tabla" de la agenda del día. Misma data que la grilla (columnas de
   `buildAgendaModel`), con la estructura y las acciones de la tabla de Canchas: filtro de Estado + buscador,
   `zr-table` / `zr-badge`, Pago / Seña, paginación de 10 y tarjetas en mobile. Las acciones las resuelve AgendaTab.
───────────────────────────────────────── */

const ITEMS_POR_PAGINA = 10;

const OPCIONES_ESTADO = [
  { value: "all", label: "Todos" },
  { value: "pending", label: "Pendientes" },
  { value: "confirmed", label: "Confirmados" },
  { value: "present", label: "Presentes" },
  { value: "no_show", label: "No asistió" },
  { value: "cancelled", label: "Cancelados" },
];

const BADGE_POR_TONO = {
  present: "zr-badge zr-badge--present",
  pending: "zr-badge zr-badge--pending",
  reserved: "zr-badge zr-badge--pending",
  cancelled: "zr-badge zr-badge--cancel",
  "no-show": "zr-badge zr-badge--cancel",
};

const PAGO = { paid: "Abonado", failed: "Fallido" };

const coincideEstado = (turno, filtro) => {
  if (filtro === "all") return true;
  const estado = estadoVisible(turno);
  return filtro === "pending" ? estado === "pending" || estado === "reserved" : estado === filtro;
};

const normalizar = (texto) => String(texto || "").toLowerCase();

const horario = (t) => `${formatAxisMinute(t.startMin)} a ${formatAxisMinute(t.endMin)} hs`;

/** Muted line under the time when the professional stays busy after the service. */
const NotaPreparacion = ({ t }) => {
  const minutos = minutosPreparacion(t);
  return minutos > 0 ? <span className="zr-prep-note">+ {minutos} min prep.</span> : null;
};

const estiloNota = {
  fontSize: "12px",
  fontWeight: "700",
  color: "#b1c20e",
  background: "#F6F9E8",
  border: "1px solid rgba(143, 168, 10, 0.3)",
  padding: "4px 8px",
  borderRadius: "6px",
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  marginTop: "6px",
};

const estiloPago = (t) => ({
  fontSize: "10px",
  fontWeight: "800",
  textTransform: "uppercase",
  color: t.paymentstatus === "paid" ? "#b1c20e" : t.paymentstatus === "failed" ? "#dc2626" : "#d97706",
  marginTop: "2px",
});

const EstadoBadges = ({ t, meta, badge }) => (
  <>
    <span className={badge}>
      <span className="dot" />
      {meta.label}
    </span>
    {t.bookingOrigin === "organizer" && (
      <span className="zr-badge zr-badge--origin" style={{ marginLeft: 6 }}>
        <span className="dot" />
        Carga manual
      </span>
    )}
  </>
);

/* Las mismas acciones que Canchas (Confirmar, Marcar ingreso, Cancelar, No Show, Mensaje) + Mover, de Beauty. */
const Acciones = ({ t, nombre, movil, onAccion, onMover }) => {
  const extra = movil ? { padding: "6px 10px", fontSize: 13, flex: "1 1 auto", whiteSpace: "nowrap", textAlign: "center" } : undefined;
  const pill = (tipo, label, className = "zr-pill-btn") => (
    <button type="button" className={className} style={extra} aria-label={`${label} de ${nombre}`} onClick={() => onAccion(tipo, t)}>
      {label}
    </button>
  );
  return (
    <>
      {puedeConfirmar(t) && (
        <button
          type="button"
          className="zr-btn zr-btn--lime"
          style={movil ? { ...extra, padding: "6px 12px" } : { height: 30, fontSize: 12 }}
          aria-label={`Confirmar turno de ${nombre}`}
          onClick={() => onAccion("confirm", t)}
        >
          Confirmar
        </button>
      )}
      {puedeMarcarIngreso(t) && pill("checkin", "Marcar ingreso", "zr-pill-btn zr-pill-btn--dark zr-reservation-action")}
      {puedeMover(t) && (
        <button type="button" className="zr-pill-btn" style={extra} aria-label={`Mover turno de ${nombre}`} onClick={() => onMover(t)}>
          Mover
        </button>
      )}
      {puedeCancelar(t) && !t.isPast && pill("cancel", "Cancelar")}
      {puedeNoShow(t) && pill("no-show", "No Show")}
      <WhatsappLink phone={t.phone} name={nombre} className="zr-pill-btn zr-pill-btn--whatsapp" />
    </>
  );
};

export default function BeautyAgendaTabla({
  model,
  mostrarCancelados = false,
  onTurnoSelect = () => {},
  onAccion = () => {},
  onMover = () => {},
}) {
  const [estado, setEstado] = useState("all");
  const [busqueda, setBusqueda] = useState("");
  const [pagina, setPagina] = useState(1);

  const turnos = useMemo(
    () =>
      model.columns
        .flatMap((col) => col.appointments.map((a) => ({ ...a, profesional: a.professionalName || col.name })))
        .filter((a) => mostrarCancelados || a.status !== "cancelled")
        .sort((a, b) => a.startMin - b.startMin || String(a.profesional).localeCompare(String(b.profesional))),
    [model, mostrarCancelados]
  );

  const visibles = useMemo(() => {
    const q = normalizar(busqueda).trim();
    return turnos.filter(
      (t) =>
        coincideEstado(t, estado) &&
        (!q || [nombreCliente(t), t.phone, serviciosLabel(t), t.profesional].some((campo) => normalizar(campo).includes(q)))
    );
  }, [turnos, estado, busqueda]);

  // Same as Canchas: back to the first page when a filter changes the result set.
  useEffect(() => setPagina(1), [estado, busqueda, mostrarCancelados]);

  const totalPaginas = Math.max(1, Math.ceil(visibles.length / ITEMS_POR_PAGINA));
  const paginaActual = Math.min(pagina, totalPaginas);
  const enPagina = useMemo(
    () => visibles.slice((paginaActual - 1) * ITEMS_POR_PAGINA, paginaActual * ITEMS_POR_PAGINA),
    [visibles, paginaActual]
  );

  const datos = (t) => {
    const meta = statusMeta(estadoVisible(t));
    return {
      meta,
      nombre: nombreCliente(t),
      servicios: serviciosLabel(t) || "-",
      badge: BADGE_POR_TONO[meta.tone] || "zr-badge",
      conSena: Number(t.depositAmount) > 0,
    };
  };

  // The name opens the turno detail (not offered for cancelled ones, as before).
  const nombreNode = (t, nombre) =>
    t.status !== "cancelled" ? (
      <button type="button" className="zr-client__name ba-tabla__nombre" aria-label={`Ver turno de ${nombre}`} onClick={() => onTurnoSelect(t)}>
        {nombre}
      </button>
    ) : (
      <div className="zr-client__name">{nombre}</div>
    );

  return (
    <>
      <div className="zr-filters">
        <Row className="g-4 align-items-end">
          <Col md={4}>
            <div className="zr-field">
              <span className="zr-field__label">Estado</span>
              <OrganizerPillSelect value={estado} onChange={setEstado} options={OPCIONES_ESTADO} placeholder="Todos" />
            </div>
          </Col>
          <Col md={8}>
            <div className="zr-field">
              <span className="zr-field__label">Buscar cliente</span>
              <label className="zr-search" style={{ height: 40, marginBottom: 0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.2-3.2" />
                </svg>
                <input
                  type="text"
                  placeholder="Buscar por nombre, teléfono o servicio..."
                  aria-label="Buscar cliente"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </label>
            </div>
          </Col>
        </Row>
      </div>

      {visibles.length === 0 ? (
        <div className="zr-panel" style={{ padding: 0 }}>
          <div className="zr-empty">No se encontraron turnos. Probá cambiando el día o el estado.</div>
        </div>
      ) : (
        <div className="zr-table-wrap">
          <table className="zr-table zr-desktop-only">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Hora</th>
                <th>Servicios</th>
                <th>Profesional</th>
                <th>Pago / Seña</th>
                <th>Estado</th>
                <th className="right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {enPagina.map((t) => {
                const { meta, nombre, servicios, badge, conSena } = datos(t);
                return (
                  <tr key={t._id}>
                    <td>
                      <div className="zr-client">
                        {nombreNode(t, nombre)}
                        <div className="zr-client__meta">Tel: {telefonoParaMostrar(t.phone) || "-"}</div>
                        {t.notes && <div style={estiloNota}>💬 "{t.notes}"</div>}
                        <RecordatorioLineas reminder={t.reminder} className="zr-recordatorios--fila" />
                      </div>
                    </td>
                    <td style={{ fontWeight: 700, fontSize: 13 }}>
                      {horario(t)}
                      <SerieChip serie={t.series} />
                      <NotaPreparacion t={t} />
                    </td>
                    <td>{servicios}</td>
                    <td>{t.profesional || "-"}</td>
                    <td className="muted">
                      {conSena ? (
                        <>
                          <div style={{ fontWeight: "700", color: "#111827" }}>{plata(t.depositAmount)}</div>
                          <div style={estiloPago(t)}>{PAGO[t.paymentstatus] || "Pendiente"}</div>
                        </>
                      ) : (
                        <span style={{ color: "#9ca3af", fontWeight: "700" }}>Sin seña</span>
                      )}
                    </td>
                    <td>
                      <EstadoBadges t={t} meta={meta} badge={badge} />
                    </td>
                    <td className="right">
                      <div style={{ display: "inline-flex", gap: "6px" }}>
                        <Acciones t={t} nombre={nombre} onAccion={onAccion} onMover={onMover} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <TablePagination page={paginaActual} pageSize={ITEMS_POR_PAGINA} totalItems={visibles.length} onPageChange={setPagina} noun="turnos" />

          <div className="zr-mobile-only zr-res-cards-mobile">
            {enPagina.map((t) => {
              const { meta, nombre, servicios, badge, conSena } = datos(t);
              return (
                <div key={t._id} className="zr-res-card-mobile">
                  <div className="zr-res-card-mobile__header">
                    <div>
                      <span className="code">{horario(t)}</span>
                      <SerieChip serie={t.series} />
                      <NotaPreparacion t={t} />
                    </div>
                    <div className="zr-res-card-mobile__badges">
                      <EstadoBadges t={t} meta={meta} badge={badge} />
                    </div>
                  </div>
                  <div className="zr-res-card-mobile__body">
                    {nombreNode(t, nombre)}
                    <div className="zr-client__meta">Tel: {telefonoParaMostrar(t.phone) || "-"}</div>
                    {t.notes && <div style={{ ...estiloNota, fontSize: "11px", display: "inline-block" }}>💬 "{t.notes}"</div>}
                    <hr className="zr-card-divider" />
                    <div className="zr-res-card-mobile__details">
                      <div className="detail-item">
                        <strong>Servicios:</strong> {servicios}
                      </div>
                      <div className="detail-item">
                        <strong>Profesional:</strong> {t.profesional || "-"}
                      </div>
                      <div className="detail-item">
                        <strong>Pago:</strong>{" "}
                        {conSena ? (
                          <span>
                            {plata(t.depositAmount)} ({PAGO[t.paymentstatus] || "Pendiente"})
                          </span>
                        ) : (
                          <span style={{ color: "#9ca3af" }}>Sin seña</span>
                        )}
                      </div>
                      <RecordatorioLineas reminder={t.reminder} className="detail-item" />
                    </div>
                  </div>
                  <div className="zr-res-card-mobile__footer" style={{ flexWrap: "wrap", gap: "8px" }}>
                    <Acciones t={t} nombre={nombre} movil onAccion={onAccion} onMover={onMover} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
